(() => {
  if (customElements.get('subscription-form-v2')) return;

  class SubscriptionFormV2 extends HTMLElement {
    constructor() {
      super();
      this.onCustomSelectTriggerClick = this.onCustomSelectTriggerClick.bind(this);
      this.onCustomSelectOptionClick = this.onCustomSelectOptionClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onOneTimeClick = this.onOneTimeClick.bind(this);
    }

    connectedCallback() {
      this.customSelect = this.querySelector('.js-subscription-form-v2-schedule');
      this.sellingPlanInput = this.querySelector('.js-subscription-form-v2-selling-plan');
      this.subscribePriceEl = this.querySelector('.js-subscription-form-v2-subscribe-price');
      this.originalPriceEl = this.querySelector('.js-subscription-form-v2-original-price');
      this.onetimePriceEl = this.querySelector('.js-subscription-form-v2-onetime-price');
      this.onetimeButton = this.querySelector('.js-subscription-form-v2-onetime');
      this.discountEl = this.querySelector('.js-subscription-form-v2-discount');
      this.percentBadgeEl = this.querySelector('.js-subscription-form-v2-percent-badge');

      if (this.customSelect) {
        this.customSelect
          .querySelector('.subscription-form-v2__select-trigger')
          ?.addEventListener('click', this.onCustomSelectTriggerClick);
        this.customSelect
          .querySelectorAll('.subscription-form-v2__select-option')
          .forEach((opt) => opt.addEventListener('click', this.onCustomSelectOptionClick));
        document.addEventListener('click', this.onDocumentClick);
      }

      this.onetimeButton?.addEventListener('click', this.onOneTimeClick);

      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      requestAnimationFrame(() => this.initState());
    }

    setOnetimeDisabled(disabled) {
      if (!this.onetimeButton) return;
      this.onetimeButton.disabled = Boolean(disabled);
    }

    disconnectedCallback() {
      if (this.customSelect) {
        this.customSelect
          .querySelector('.subscription-form-v2__select-trigger')
          ?.removeEventListener('click', this.onCustomSelectTriggerClick);
        this.customSelect
          .querySelectorAll('.subscription-form-v2__select-option')
          .forEach((opt) => opt.removeEventListener('click', this.onCustomSelectOptionClick));
        document.removeEventListener('click', this.onDocumentClick);
      }
      this.onetimeButton?.removeEventListener('click', this.onOneTimeClick);
      this.variantChangeUnsubscriber?.();
    }

    initState() {
      if (this.sellingPlanInput && !this.sellingPlanInput.value && this.customSelect?.dataset.value) {
        this.sellingPlanInput.value = this.customSelect.dataset.value;
      }

      const resolved = this.resolveCurrentPrices();
      if (resolved) this.updateWidgetPriceDisplay(resolved.subPrice, resolved.originalPrice);

      this.updateProductPriceBlock(resolved?.subPrice, resolved?.originalPrice);
      this.updateAtcLabel();
      const currentVariant = this.resolveCurrentVariant();
      if (currentVariant) this.setOnetimeDisabled(currentVariant.available === false);

      if (typeof getPriceByVariantAndPlan === 'function') {
        getPriceByVariantAndPlan();
      }
    }

    updateURL(planId) {
      if (this.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      if (planId) {
        url.searchParams.set('selling_plan', planId);
      } else {
        url.searchParams.delete('selling_plan');
      }
      history.replaceState(history.state, '', url.toString());
    }

    onCustomSelectTriggerClick(event) {
      event.stopPropagation();
      const isOpen = this.customSelect.classList.toggle('is-open');
      this.customSelect
        .querySelector('.subscription-form-v2__select-trigger')
        ?.setAttribute('aria-expanded', String(isOpen));
    }

    onCustomSelectOptionClick(event) {
      this.selectCustomOption(event.currentTarget, true);
    }

    selectCustomOption(optionEl, doSave) {
      const value = optionEl.dataset.value;
      const text = optionEl.textContent.trim();

      this.customSelect.querySelectorAll('.subscription-form-v2__select-option').forEach((opt) => {
        const selected = opt === optionEl;
        opt.classList.toggle('is-selected', selected);
        opt.setAttribute('aria-selected', String(selected));
      });

      const valueEl = this.customSelect.querySelector('.subscription-form-v2__select-value');
      if (valueEl) valueEl.textContent = text;

      this.customSelect.dataset.value = value;
      this.customSelect.classList.remove('is-open');
      this.customSelect
        .querySelector('.subscription-form-v2__select-trigger')
        ?.setAttribute('aria-expanded', 'false');

      if (optionEl.dataset.discountPercent !== undefined) {
        this.dataset.discountPercent = optionEl.dataset.discountPercent;
      }

      if (this.sellingPlanInput) this.sellingPlanInput.value = value;

      if (doSave) {
        this.updateURL(value);
        const resolved = this.resolveCurrentPrices();
        if (resolved) this.updateWidgetPriceDisplay(resolved.subPrice, resolved.originalPrice);
        this.updateProductPriceBlock(resolved?.subPrice, resolved?.originalPrice);
        this.updateAtcLabel();
        if (typeof getPriceByVariantAndPlan === 'function') {
          getPriceByVariantAndPlan();
        }
      }
    }

    onDocumentClick(event) {
      if (!this.customSelect?.contains(event.target)) {
        this.customSelect?.classList.remove('is-open');
        this.customSelect
          ?.querySelector('.subscription-form-v2__select-trigger')
          ?.setAttribute('aria-expanded', 'false');
      }
    }

    onVariantChange(event) {
      if (event.data.sectionId !== this.dataset.sectionId) return;
      const variant = event.data.variant;
      this.setOnetimeDisabled(!variant || variant.available === false);
      if (!variant) return;
      this.updateVariantPrices(variant);
      this.updateURL(this.customSelect?.dataset.value || null);
    }

    onOneTimeClick() {
      if (this.onetimeButton?.disabled || this.onetimeButton?.classList.contains('is-loading')) return;

      const form = document.getElementById(`product-form-${this.dataset.sectionId}`);
      const variantId = form?.querySelector('.product-variant-id')?.value;
      if (!variantId || !form) return;

      const quantityInput = form.querySelector('[name="quantity"]');
      const quantity = quantityInput ? quantityInput.value : 1;

      this.onetimeButton.classList.add('is-loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'subscription-form-v2',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            return;
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'subscription-form-v2',
            productVariantId: variantId,
            cartData: response,
          });

          cart.renderContents(response);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.onetimeButton?.classList.remove('is-loading');
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        });
    }

    updateVariantPrices(variant) {
      if (!variant) {
        const resolved = this.resolveCurrentPrices();
        if (resolved) {
          this.updateWidgetPriceDisplay(resolved.subPrice, resolved.originalPrice);
          this.updatePerNightBadge(resolved.subPrice, resolved.originalPrice);
        }
        return;
      }

      const currentPlanId = this.customSelect?.dataset.value || null;
      const discountPercent = parseFloat(
        this.customSelect?.querySelector('.subscription-form-v2__select-option.is-selected')?.dataset.discountPercent ||
          this.dataset.discountPercent ||
          0
      );

      let subPrice = null;
      let originalPrice = variant.price;

      if (currentPlanId && Array.isArray(variant.selling_plan_allocations)) {
        const allocation = variant.selling_plan_allocations.find(
          (a) => String(a.selling_plan_id) === String(currentPlanId)
        );
        if (allocation) {
          subPrice = allocation.price;
          originalPrice = allocation.compare_at_price || variant.price;
        }
      }

      if (subPrice === null && discountPercent > 0) {
        subPrice = Math.round(variant.price * (1 - discountPercent / 100));
      }

      this.updateWidgetPriceDisplay(subPrice, originalPrice);
      this.resetPriceBlockCache();
      this.updateProductPriceBlock(subPrice, originalPrice);
      this.updateAtcLabel();
    }

    updateAtcLabel() {
      const section =
        document.getElementById(`MainProduct-${this.dataset.sectionId}`) ||
        document.getElementById(`FeaturedProduct-${this.dataset.sectionId}`);
      if (!section) return;

      const button = section.querySelector('[id^="ProductSubmitButton"]');
      const span = button?.querySelector('span');
      if (!span || button.hasAttribute('disabled')) return;

      const priceBlock = section.querySelector('.product_price_block');
      const priceEl =
        priceBlock?.querySelector('.price__sale .price-item--sale') ||
        priceBlock?.querySelector('.price__sale .price-item--last') ||
        priceBlock?.querySelector('.price__regular .price-item--regular') ||
        priceBlock?.querySelector('.price-item--last');

      const label = section.dataset.atcLabel || 'Add to Cart';
      const price = priceEl ? priceEl.textContent.replace(/\s+/g, ' ').trim() : '';
      span.textContent = price ? `${label} - ${price}` : label;
    }

    updateWidgetPriceDisplay(subPrice, originalPrice) {
      if (subPrice !== null && this.subscribePriceEl) {
        this.subscribePriceEl.textContent = this.formatMoney(subPrice);
      }
      if (this.originalPriceEl) {
        this.originalPriceEl.textContent = this.formatMoney(originalPrice);
      }
      if (this.onetimePriceEl) {
        this.onetimePriceEl.textContent = this.formatMoney(originalPrice);
      }
      if (subPrice !== null) this.updateDiscountDisplay(subPrice, originalPrice);
      this.updatePercentBadge(subPrice, originalPrice);
      this.updatePerNightBadge(subPrice, originalPrice);
      this.updatePriceDiffBadge(subPrice, originalPrice);
    }

    updatePercentBadge(subPrice, originalPrice) {
      if (this.dataset.showDiscountPercent !== 'true' || !this.percentBadgeEl) return;

      let pct = 0;
      if (subPrice != null && originalPrice > 0 && subPrice < originalPrice) {
        pct = Math.round(((originalPrice - subPrice) / originalPrice) * 100);
      } else {
        const selectedOption = this.customSelect?.querySelector('.subscription-form-v2__select-option.is-selected');
        pct = Math.round(parseFloat(selectedOption?.dataset.discountPercent || this.dataset.discountPercent || 0));
      }

      if (pct > 0) {
        this.percentBadgeEl.textContent = `-${pct}%`;
        this.percentBadgeEl.hidden = false;
      } else {
        this.percentBadgeEl.hidden = true;
      }
    }

    updatePriceDiffBadge(subPrice, originalPrice) {
      const priceBlock = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceBlock) return;

      const badge = priceBlock.querySelector('.price-diff-badge');
      if (!badge) return;

      const variantDataEl = document.querySelector(
        `variant-radios[data-section="${this.dataset.sectionId}"] script[type="application/json"],
         variant-selects[data-section="${this.dataset.sectionId}"] script[type="application/json"]`
      );
      const variantId = document.querySelector(`#product-form-${this.dataset.sectionId} .product-variant-id`)?.value;

      let topComparePrice = originalPrice;
      if (variantDataEl && variantId) {
        try {
          const variants = JSON.parse(variantDataEl.textContent);
          const variant = variants.find((v) => String(v.id) === String(variantId));
          if (variant) {
            topComparePrice = variant.compare_at_price || variant.price;
          }
        } catch (e) {}
      }

      const diff = Math.max(0, topComparePrice - subPrice);
      if (diff > 0) {
        const wholeDollars = Math.round(diff / 100) * 100;
        const formatted = this.formatMoney(wholeDollars).replace(/[.,]00(?=[^0-9]|$)/, '');
        badge.textContent = `${formatted} OFF`;
        badge.style.display = 'inline-block';
      } else {
        badge.style.display = 'none';
      }
    }

    updatePerNightBadge(subPrice, originalPrice) {
      const priceBlock = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceBlock) return;

      const badge = priceBlock.querySelector('.pernight-bedge:not(.price-diff-badge)');
      if (!badge) return;

      const variantDataEl = document.querySelector(
        `variant-radios[data-section="${this.dataset.sectionId}"] script[type="application/json"],
         variant-selects[data-section="${this.dataset.sectionId}"] script[type="application/json"]`
      );
      const variantId = document.querySelector(`#product-form-${this.dataset.sectionId} .product-variant-id`)?.value;

      const optionDays = this.resolveNightsPerUnit(priceBlock, variantId, variantDataEl);
      const perNight = subPrice / optionDays;
      const formatted = this.formatMoney(perNight);
      const currentText = badge.textContent.trim();
      const textSuffix = currentText.replace(/[0-9.,$£€₸\s]+/g, '').trim();
      badge.textContent = `${formatted} ${textSuffix}`;
    }

    resolveNightsPerUnit(priceBlock, variantId, variantDataEl) {
      const priceEl = priceBlock?.querySelector?.('.price') || priceBlock;
      const raw = priceEl?.dataset?.variantNights;
      if (raw && variantId) {
        try {
          const nightsMap = JSON.parse(raw);
          const nights = nightsMap[String(variantId)];
          if (nights != null && Number(nights) > 0) return Number(nights);
        } catch (e) {}
      }

      let optionDays = 30;
      if (variantDataEl && variantId) {
        try {
          const variants = JSON.parse(variantDataEl.textContent);
          const variant = variants.find((v) => String(v.id) === String(variantId));
          if (variant && variant.options) {
            variant.options.forEach((opt) => {
              const val = String(opt).toLowerCase();
              if (val.includes('2-pack')) optionDays = 60;
              else if (val.includes('3-pack')) optionDays = 90;
              else if (val.includes('4-pack')) optionDays = 120;
            });
          }
        } catch (e) {}
      }
      return optionDays;
    }

    updateDiscountDisplay(subPrice, originalPrice) {
      if (!this.discountEl || this.dataset.showDiscount !== 'true') return;
      const diff = originalPrice - subPrice;
      if (diff <= 0) {
        this.discountEl.textContent = '';
        return;
      }
      if (this.dataset.discountType === 'amount') {
        const wholeDollars = Math.round(diff / 100) * 100;
        const formatted = this.formatMoney(wholeDollars).replace(/[.,]00(?=[^0-9]|$)/, '');
        this.discountEl.textContent = `Save ${formatted}`;
      } else {
        const pct = Math.round((diff / originalPrice) * 100);
        this.discountEl.textContent = `Save ${pct}%`;
      }
    }

    resolveCurrentVariant() {
      const variantDataEl = document.querySelector(
        `variant-radios[data-section="${this.dataset.sectionId}"] script[type="application/json"],
         variant-selects[data-section="${this.dataset.sectionId}"] script[type="application/json"]`
      );
      const variantId = document.querySelector(`#product-form-${this.dataset.sectionId} .product-variant-id`)?.value;
      if (!variantDataEl || !variantId) return null;

      try {
        const variants = JSON.parse(variantDataEl.textContent);
        return variants.find((v) => String(v.id) === String(variantId)) || null;
      } catch (e) {
        return null;
      }
    }

    resolveCurrentPrices() {
      const currentPlanId = this.customSelect?.dataset.value || null;
      const variantDataEl = document.querySelector(
        `variant-radios[data-section="${this.dataset.sectionId}"] script[type="application/json"],
         variant-selects[data-section="${this.dataset.sectionId}"] script[type="application/json"]`
      );
      const variantId = document.querySelector(`#product-form-${this.dataset.sectionId} .product-variant-id`)?.value;
      if (!variantDataEl || !variantId) return null;

      try {
        const variants = JSON.parse(variantDataEl.textContent);
        const variant = variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return null;

        if (currentPlanId && Array.isArray(variant.selling_plan_allocations)) {
          const allocation = variant.selling_plan_allocations.find(
            (a) => String(a.selling_plan_id) === String(currentPlanId)
          );
          if (allocation) {
            return { subPrice: allocation.price, originalPrice: allocation.compare_at_price || variant.price };
          }
        }

        const discountPercent = parseFloat(
          this.customSelect?.querySelector('.subscription-form-v2__select-option.is-selected')?.dataset.discountPercent ||
            this.dataset.discountPercent ||
            0
        );
        return {
          subPrice: discountPercent > 0 ? Math.round(variant.price * (1 - discountPercent / 100)) : variant.price,
          originalPrice: variant.price,
        };
      } catch (e) {
        return null;
      }
    }

    resetPriceBlockCache() {
      const priceBlock = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceBlock) return;
      const priceEl = priceBlock.querySelector('.price');
      if (!priceEl) return;
      priceEl.classList.remove('subscription-active');
      delete priceEl.dataset.originalOnSale;
      priceEl.querySelectorAll('[data-original]').forEach((el) => el.removeAttribute('data-original'));
    }

    updateProductPriceBlock(subPrice, originalPrice) {
      const priceBlock = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceBlock) return;
      const priceEl = priceBlock.querySelector('.price');
      if (!priceEl) return;

      const saleEl = priceEl.querySelector('.price__sale');
      const saleItem = saleEl?.querySelector('.price-item--sale');
      let compareItem = saleEl?.querySelector('s.price-item--regular, .price-item--regular:not(.price-item--sale)');
      const regularItem = priceEl.querySelector('.price__regular .price-item--regular');

      if (subPrice === undefined) {
        const resolved = this.resolveCurrentPrices();
        if (!resolved) return;
        subPrice = resolved.subPrice;
        originalPrice = resolved.originalPrice;
      }

      if (!priceEl.classList.contains('subscription-active')) {
        if (saleItem) saleItem.dataset.original = saleItem.textContent.trim();
        if (compareItem) compareItem.dataset.original = compareItem.textContent.trim();
        if (regularItem) regularItem.dataset.original = regularItem.textContent.trim();
        priceEl.dataset.originalOnSale = priceEl.classList.contains('price--on-sale') ? '1' : '0';
        priceEl.classList.add('subscription-active');
      }

      priceEl.classList.add('price--on-sale');
      if (saleItem) saleItem.textContent = this.formatMoney(subPrice);
      if (compareItem) compareItem.textContent = this.formatMoney(originalPrice);
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, window.Shopify.moneyFormat || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }
  }

  customElements.define('subscription-form-v2', SubscriptionFormV2);
})();
